import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import {
  buildJurisdictionPayloadFromForm,
  mapCountryOptionsResponseToDto,
  mapJurisdictionsResponseToDto,
} from '../dto/system-jurisdictions.dto.js';
import { buildEmptyJurisdictionForm, buildJurisdictionsTableModel } from '../model/system-jurisdictions.model.js';

function mapFieldErrors(requestError, fields) {
  const nextFieldErrors = {};
  const details = Array.isArray(requestError?.errors) ? requestError.errors : [];

  details.forEach((item) => {
    const parameter = String(item?.parameter || '').trim();
    if (fields.includes(parameter)) {
      nextFieldErrors[parameter] = String(item?.message || 'Invalid value').trim() || 'Invalid value';
    }
  });

  return nextFieldErrors;
}

export function useSystemJurisdictions() {
  const { token } = useAuth();

  const [rows, setRows] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [detailsTargetId, setDetailsTargetId] = useState('');
  const [editingTargetId, setEditingTargetId] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState('');
  const [form, setForm] = useState(buildEmptyJurisdictionForm);
  const [formErrors, setFormErrors] = useState({});

  const detailsRow = useMemo(
    () => rows.find((row) => row.id === detailsTargetId) || null,
    [rows, detailsTargetId],
  );

  const loadJurisdictions = useCallback(async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await adminApi.getJurisdictions(token);
      const dto = mapJurisdictionsResponseToDto(response);
      setRows(buildJurisdictionsTableModel(dto));
    } catch (requestError) {
      if (requestError?.status === 404) {
        setRows([]);
      } else {
        setRows([]);
        setError(requestError.message || 'Failed to load jurisdictions');
      }
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  const loadCountries = useCallback(async () => {
    try {
      const response = await adminApi.getCountryDictionary(token);
      setCountryOptions(mapCountryOptionsResponseToDto(response));
    } catch {
      setCountryOptions([]);
    }
  }, [token]);

  useEffect(() => {
    loadJurisdictions();
    loadCountries();
  }, [loadCountries, loadJurisdictions]);

  const closeModals = useCallback(() => {
    if (isSaving) {
      return;
    }

    setDetailsTargetId('');
    setEditingTargetId('');
    setIsCreateOpen(false);
    setDeleteTargetId('');
    setForm(buildEmptyJurisdictionForm());
    setFormErrors({});
  }, [isSaving]);

  const openCreate = useCallback(() => {
    setDetailsTargetId('');
    setEditingTargetId('');
    setDeleteTargetId('');
    setIsCreateOpen(true);
    setForm(buildEmptyJurisdictionForm());
    setFormErrors({});
    setError('');
    setSuccess('');
  }, []);

  const openEdit = useCallback((row) => {
    setDetailsTargetId('');
    setDeleteTargetId('');
    setIsCreateOpen(false);
    setEditingTargetId(row.id);
    setForm({
      name: row.name,
      byDefault: Boolean(row.byDefault),
      countries: Array.isArray(row.countries) ? row.countries : [],
    });
    setFormErrors({});
    setError('');
    setSuccess('');
  }, []);

  const openDetails = useCallback((id) => {
    setDetailsTargetId(id);
  }, []);

  const requestDelete = useCallback((id) => {
    setDeleteTargetId(id);
  }, []);

  const onFormChange = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFormErrors((prev) => {
      if (!prev[key]) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const onCountriesChange = useCallback((event) => {
    const values = Array.from(event.target.selectedOptions).map((option) => option.value);
    onFormChange('countries', values);
  }, [onFormChange]);

  const saveForm = useCallback(async (event) => {
    event.preventDefault();

    const payload = buildJurisdictionPayloadFromForm(form);
    const nextErrors = {};
    if (!payload.name) {
      nextErrors.name = 'This field is required.';
    }

    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      setError('Please fix validation errors.');
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      if (editingTargetId) {
        await adminApi.updateJurisdiction(token, editingTargetId, payload);
      } else {
        await adminApi.saveJurisdiction(token, payload);
      }
      await loadJurisdictions();

      setIsCreateOpen(false);
      setEditingTargetId('');
      setForm(buildEmptyJurisdictionForm());
      setFormErrors({});
      setSuccess(editingTargetId ? 'Jurisdiction updated.' : 'Jurisdiction created.');
    } catch (requestError) {
      setFormErrors(mapFieldErrors(requestError, ['name', 'byDefault', 'countries', 'id']));
      setError(requestError.message || 'Failed to save jurisdiction');
    } finally {
      setIsSaving(false);
    }
  }, [editingTargetId, form, loadJurisdictions, token]);

  const confirmDelete = useCallback(async () => {
    if (!deleteTargetId || isSaving) {
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      await adminApi.deleteJurisdiction(token, deleteTargetId);
      setDeleteTargetId('');
      await loadJurisdictions();
      setSuccess('Jurisdiction deleted.');
    } catch (requestError) {
      setError(requestError.message || 'Failed to delete jurisdiction');
    } finally {
      setIsSaving(false);
    }
  }, [deleteTargetId, isSaving, loadJurisdictions, token]);

  return {
    rows,
    countryOptions,
    isLoading,
    isSaving,
    error,
    success,
    detailsRow,
    editingTargetId,
    isCreateOpen,
    deleteTargetId,
    form,
    formErrors,
    closeModals,
    openCreate,
    openEdit,
    openDetails,
    requestDelete,
    onFormChange,
    onCountriesChange,
    saveForm,
    confirmDelete,
  };
}

